import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createSimilarToVideoSkill: Skill = {
    name: SKILL_NAMES.createSimilarToVideo,
    description:
        'Create a video that matches the style of a reference video. Load whenever the user has selected a blueprint or wants a video "like" an existing one.',
    kind: 'video',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Create Similar To Video (Blueprints & Reference Videos)

A reference video is one the user wants new videos to look and feel like (often a blueprint's example video).

Your job is to write NEW content in the reference video's style — matching its script tone and structure AND its visual choices — never to copy the reference video's content.

Also load the recipe matching the narrative thread you'll build with; this recipe only covers what's different when imitating a reference video.

## Step 1 — Fetch the reference video
Call \`${TOOL_NAMES.fetchReferenceVideo}\` BEFORE creating the blank video — the reference decides your template, aspect ratio, and structure defaults.

## Step 2 — Analyze the reference before building
Study the annotation and write down (for yourself) the patterns you'll replicate:

**Script style:**
- Tone and voice (playful vs corporate, first vs second person, direct address, questions vs statements)
- Structure (how it hooks, how ideas progress, how it ends — CTA? punchline? summary?)
- Sentence length and how much text appears per scene
- Total length — target a similar word count and scene count unless the user asked for a different duration

**Visual choices:**
- Which scene designs are used, in what order, and which repeat
- Intro: unless the content is significantly different, match the scene design & content structure for the intro 
- Outro: unless explicitly asked, MATCH the outro scene design and content structure (CTA, summary, etc.) to the reference
- Media style (photos vs footage vs solid backgrounds, subject matter, mood), icon usage, subscene usage
- Pacing (scene durations)

## Step 3 — Resolve settings by precedence
When settings conflict, apply this order (highest wins):
1. What the user explicitly asked for
2. The blueprint's custom instructions (if provided)
3. The reference video's properties

When neither the user nor their controls specify something, default to the blueprint/reference (its template, aspect ratio, narrative thread, voice, and duration). If the user's constraints contradict the reference in a way that undermines the imitation (e.g. reference is a 60s landscape text video but controls say vertical voiceover), ask with \`${TOOL_NAMES.askUserQuestion}\` whether to match the reference's format or keep their settings — don't silently produce something that resembles neither.

## Gathering the new video's content
The reference video supplies the STYLE; the user must supply the SUBJECT/CONTENT.
- If the user provided a topic, document, or URL, use it. When they provided source material, stay faithful to it — do not include information, quotes, or data points not found in it.
- If the user selected a blueprint or reference video but gave no topic, ask in plain chat — NOT with \`${TOOL_NAMES.askUserQuestion}\`, since an open-ended topic has no options to offer. Briefly say what you picked up from the reference's style, then ask what the new video should be about, mentioning they can type a topic, paste a script or a link, or attach a document.
- If the source material is too long, ask what to focus on.

## Building
- Use the reference's template when possible — its exact scene designs are then available to you. Reuse the reference's scene design ids (via the \`design\` shown on each reference scene) for the same content roles: its intro design for your intro, its stat design for your stat, etc.
- If you must use a different template or aspect ratio, the reference's design ids won't exist; pick the closest equivalents to the reference's layouts.
- Segment your script the way the reference segments its own: similar text per scene, similar use of subscenes.
- Match media style: search for media with the same mood and subject-matter treatment as the reference, not just literal keyword matches on your new script.
- Match pacing: set scene durations in line with the reference's rhythm — keep text legible; don't cut scenes too short to read.

## Verify
Before finishing, compare your video against the reference annotation: similar scene count and segmentation, intro/outro pattern honored, scene design variety mirrors the reference, pacing comparable. Fix mismatches.`;
    },
};
